import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { ButtonCancel, ButtonSave } from '@adflow/frontend/ui-buttons';
import { PasswordInput } from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { Modal } from '@adflow/frontend/ui-modal';
import { theme, typography } from '@adflow/frontend/ui-theme';
import {
	createErrorToast,
	createSuccessToast,
} from '@adflow/frontend/ui-toasts';
import {
	changePassword,
	useCheckResetPasswordTokenQuery,
	useMutationLogin,
	useRedirectFromWelcomePage,
} from '@adflow/frontend/util-auth';
import { ChangePasswordFormDto } from '@adflow/shared/util-auth';

import { RESET_PASSWORD_MODAL_PARAM } from './ResetPassword';

const formSchema = ChangePasswordFormDto;
type formValues = ChangePasswordFormDto;

const styles = {
	inputField: css({ marginTop: '1rem' }),
	icon: css({
		color: theme.colors.lightBlue,
		marginLeft: '0.5rem',
		cursor: 'pointer',
	}),
	subtitle: css({ marginBottom: '1.25rem' }, typography.subTitle),
};

export function WelcomeView() {
	const { t } = useTranslation(['login', 'user-form']);
	const navigate = useNavigate();

	const { mutateAsync: requestLogin } = useMutationLogin();

	const formMethods = useForm<formValues>({
		defaultValues: { confirmPassword: '', newPassword: '' },
		resolver: superstructResolver(formSchema, { coerce: true, mask: true }),
	});
	const { token = '' } = useParams();

	const { data: checkTokenData, isPending: tokenResponsePending } =
		useCheckResetPasswordTokenQuery(token);

	const { isValidToken, user } = checkTokenData ?? {};

	const navigateToLogin = useCallback(() => {
		void navigate({
			pathname: '/login',
			search: `?${RESET_PASSWORD_MODAL_PARAM}=t`,
		});
	}, [navigate]);

	const handleInvalidToken = useCallback(() => {
		createErrorToast(t('login:invalid password reset link'), {
			autoClose: false,
		});
		navigateToLogin();
	}, [navigateToLogin, t]);

	useEffect(() => {
		if (!tokenResponsePending && !isValidToken) {
			handleInvalidToken();
		}
	}, [handleInvalidToken, tokenResponsePending, isValidToken]);

	const redirectFromWelcomePage = useRedirectFromWelcomePage();

	const handleChangePassword = formMethods.handleSubmit(async (data) => {
		try {
			const responseData = await changePassword({
				newPassword: data.newPassword,
				token,
			});
			const { email } = responseData;

			await requestLogin({
				email,
				password: data.newPassword,
			});
			// This is a hack, but I could not get anything else to work in a
			// reasonable amount of time. -Joshua
			redirectFromWelcomePage();
			toast.dismiss();
			createSuccessToast(
				t('login:Your password has been successfully changed'),
				10
			);
		} catch {
			handleInvalidToken();
		}
	});

	return !tokenResponsePending && isValidToken ? (
		<Modal
			show
			onRequestClose={navigateToLogin}
			dialogStyleOverrides={{ width: '340px' }}
		>
			<form onSubmit={handleChangePassword}>
				<Modal.Header>{t('login:Welcome', { user })}</Modal.Header>
				<Modal.Body>
					<div css={styles.subtitle}>{t('login:Please set your password')}</div>
					<FormProvider {...formMethods}>
						<PasswordInput
							name="newPassword"
							label={t('login:New password')}
							placeholder={t('login:Enter new password')}
							autoFocus
							data-testid="new-password"
						/>

						<div css={styles.inputField}>
							<PasswordInput
								name="confirmPassword"
								label={t('login:Confirm password')}
								placeholder={t('login:Confirm password')}
								data-testid="confirm-password"
							/>
						</div>
					</FormProvider>
				</Modal.Body>
				<Modal.Footer>
					<ButtonCancel onClick={navigateToLogin} />
					<ButtonSave type="submit">{t('login:Set password')}</ButtonSave>
				</Modal.Footer>
			</form>
		</Modal>
	) : (
		<LoadingIndicator />
	);
}
